/**
 * BigQueryForm — BigQuery warehouse connection form (RHF-based, add-only).
 *
 * Single auth path: paste a GCP service account JSON key. Location is a free-text region.
 *
 * Project ID is read-only and belongs to the **account**, not to the key: it is the bucket dim
 * entered on step 2, and the server re-derives the account key from it
 * (`assertBigQueryConfigMatchesBucketDims`). The pasted key's own `project_id` therefore never
 * overwrites it — a key issued for a different project is reported as a validation error on the
 * key field (naming both projects), so the customer can see the conflict and resolve it rather
 * than silently saving a connection keyed to one project that queries another. With no account
 * project to pin (no prefill), the key's `project_id` still fills the field.
 *
 * Pure field collector: it owns RHF state and exposes `validate()` + `getValues()`
 * via an imperative handle; Test/Save are driven by the parent (the drawer header).
 */
import { zodResolver } from '@hookform/resolvers/zod';
import type { BigQueryConnectionConfig, BigQueryServiceAccountCredentials } from '@server/types';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Form, FormField } from '@/_shared/components/ui/form';
import { Input } from '@/_shared/components/ui/input';
import { Textarea } from '@/_shared/components/ui/textarea';

import {
  type FormDraft,
  useDraftDefaults,
  useResetOnChange,
  useWarehouseFormHandle,
  type WarehouseFormHandle,
} from '../shared/formHandle';
import { FormSection } from '../shared/FormSection';
import { HiddenFieldErrors } from '../shared/HiddenFieldErrors';

interface BigQueryFormProps {
  /** Values typed before the add flow remounted the form for a new account (see `useDraftDefaults`). */
  draft?: FormDraft;
  /** The add flow names the account above the form (ACT2-1031), so its identity fields are not repeated here. */
  hideIdentity?: boolean;
  /** Prefilled config from the hook — account details (project/location) from the RudderStack destination. */
  initialConfig?: BigQueryConnectionConfig;
  /** Fired whenever a field changes, so the parent can reset a stale test result. */
  onChange?: () => void;
}

const LOCATION_RE = /^[A-Za-z0-9-]+$/;

interface BigQueryFormValues {
  serviceAccountKey: string;
  location: string;
  projectId: string;
}

/** First non-empty trimmed value among the candidates, or '' if none. */
function firstNonEmpty(...vals: (string | undefined)[]): string {
  for (const val of vals) {
    const trimmed = val?.trim();
    if (trimmed) return trimmed;
  }
  return '';
}

type ParseResult = { ok: true; credentials: BigQueryServiceAccountCredentials } | { ok: false; error: string };

function parseServiceAccountKey(json: string): ParseResult | null {
  const trimmed = json.trim();
  if (!trimmed) return null;
  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    if (typeof parsed['client_email'] !== 'string' || !parsed['client_email'].trim()) {
      return { ok: false, error: 'Service account JSON must include client_email' };
    }
    if (typeof parsed['private_key'] !== 'string' || !parsed['private_key'].trim()) {
      return { ok: false, error: 'Service account JSON must include private_key' };
    }
    return { ok: true, credentials: parsed as BigQueryServiceAccountCredentials };
  } catch {
    return { ok: false, error: 'Invalid JSON' };
  }
}

const SchemaModel = z
  .object({
    serviceAccountKey: z.string(),
    location: z
      .string()
      .trim()
      .min(1, 'Location is required')
      .regex(LOCATION_RE, 'Use letters, numbers, and hyphens only'),
    projectId: z.string().trim().min(1, 'Project ID missing — paste a service account key that includes project_id'),
  })
  .superRefine((values, ctx) => {
    const trimmed = values.serviceAccountKey.trim();
    if (!trimmed) {
      ctx.addIssue({ code: 'custom', path: ['serviceAccountKey'], message: 'Service account key is required' });
      return;
    }
    const parsed = parseServiceAccountKey(trimmed);
    if (!parsed) return;
    if (!parsed.ok) {
      ctx.addIssue({ code: 'custom', path: ['serviceAccountKey'], message: parsed.error });
      return;
    }
    // `projectId` holds the account's project (the bucket dim) whenever there is one, so this
    // compares the pasted key against the account rather than against itself.
    const keyProject = parsed.credentials.project_id?.trim() ?? '';
    const accountProject = values.projectId.trim();
    if (keyProject && accountProject && keyProject !== accountProject) {
      ctx.addIssue({
        code: 'custom',
        path: ['serviceAccountKey'],
        message: `This key belongs to project "${keyProject}", but this account connects to project "${accountProject}". Paste a key for "${accountProject}", or add a separate account for "${keyProject}".`,
      });
    }
  });

export const BigQueryForm = React.forwardRef<WarehouseFormHandle, BigQueryFormProps>(function BigQueryForm(
  { draft, initialConfig, onChange, hideIdentity = false },
  ref,
): React.JSX.Element {
  const { base, defaults } = useDraftDefaults<BigQueryFormValues>(
    {
      serviceAccountKey: '',
      location: initialConfig?.location ?? 'US',
      projectId: initialConfig?.projectId ?? '',
    },
    draft,
  );
  const form = useForm<BigQueryFormValues>({
    resolver: zodResolver(SchemaModel),
    mode: 'onTouched',
    defaultValues: defaults,
  });

  // Project ID is not entered by hand. The account's own project wins outright; the pasted key
  // only fills the field when there is no account project to pin it to. A key for some other
  // project is surfaced by the schema above, never applied here.
  const keyJson = form.watch('serviceAccountKey');
  const { setValue } = form;
  React.useEffect(() => {
    const parsed = parseServiceAccountKey(keyJson);
    const next = firstNonEmpty(initialConfig?.projectId, parsed?.ok ? parsed.credentials.project_id : undefined);
    setValue('projectId', next, { shouldDirty: false, shouldValidate: false });
  }, [keyJson, initialConfig?.projectId, setValue]);

  const buildConfig = React.useCallback((values: BigQueryFormValues): BigQueryConnectionConfig => {
    const parsed = parseServiceAccountKey(values.serviceAccountKey);
    const credentials: BigQueryServiceAccountCredentials = parsed?.ok
      ? parsed.credentials
      : { client_email: '', private_key: '' };
    return { projectId: values.projectId.trim(), location: values.location.trim(), credentials };
  }, []);

  useWarehouseFormHandle(ref, form, buildConfig, base);
  // A passing test no longer reflects edited values — let the parent reset it.
  useResetOnChange(form, onChange);

  return (
    <Form {...form}>
      <form onSubmit={(e) => e.preventDefault()} className="grid gap-5" noValidate>
        {hideIdentity && (
          <HiddenFieldErrors control={form.control} fields={[{ name: 'projectId', label: 'Project id' }]} />
        )}
        <FormField
          control={form.control}
          name="serviceAccountKey"
          render={({ field }) => (
            <FormSection label="Service Account Key" description="Paste the service account's JSON key file.">
              <Textarea
                {...field}
                rows={6}
                placeholder="Paste the contents of your GCP service account JSON key file"
                className="max-h-60 overflow-auto font-mono"
              />
            </FormSection>
          )}
        />

        <FormField
          control={form.control}
          name="location"
          render={({ field }) => (
            <FormSection label="Location" description="For example US or EU.">
              <Input {...field} placeholder="US" />
            </FormSection>
          )}
        />

        {!hideIdentity && (
          <FormField
            control={form.control}
            name="projectId"
            render={({ field }) => (
              <FormSection label="Project ID" description="The service account key must belong to this project.">
                <Input {...field} readOnly aria-readonly placeholder="Derived from service account key" />
              </FormSection>
            )}
          />
        )}
      </form>
    </Form>
  );
});
